import { ChannelIcon, EmptyStateButton, Typography } from '@weareopenr/component-library';
import styles from './RulesTab.module.scss';
import { RulesCard } from '..';
import { getExternalSystemName } from 'src/helpers';
import { CreateChannelAttributeGroupFlow } from '../CreateChannelAttributeGroupFlow/CreateChannelAttributeGroupFlow';
import { useState } from 'react';
import EmptyState from '../EmptyState';
import SkeletonContainer from '../SkeletonContainer';
import { downStreamExternalSystemIntegrationTypes } from '../../constants';
import { ItemRule } from 'src/types/menu.types';
import { useItem } from 'src/hooks/useItem/useItem';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';

const RulesTab = ({ itemId }: { itemId: string }) => {
  const { data: menuItem } = useItem(itemId);
  const { data: externalSystems, isLoading: isExternalSystemsLoading } = useExternalSystems();

  const [isChannelAttributeGroupFlowOpen, setIsChannelAttributeGroupOpen] = useState<boolean>(false);
  const addRuleLabel = '+ Channel specific attributes';
  const cardTitle = 'Channel attributes';
  const cardSubtitle = 'Set multiple product attributes and assign them to specific downstream channels.';
  const [ruleToEdit, setRuleToEdit] = useState<ItemRule | null>();

  const onClose = () => {
    setIsChannelAttributeGroupOpen(false);
    setRuleToEdit(null);
  };

  if (isExternalSystemsLoading) {
    return <SkeletonContainer hiddenTitle={true} />;
  }

  const itemDefaultAttributes = {
    consumer_name: menuItem.consumerName,
    description: menuItem.description,
    plu: menuItem.plu,
    imageUrl: menuItem.image,
  };

  // useExternalSystems resolves to undefined while the channels query is in flight and if it errors,
  // so everything below reads from this safe local rather than the raw query data.
  const allExternalSystems = externalSystems ?? [];

  // Gets an array of external systems that have rules associated with them
  const externalSystemsInRules = menuItem.rules?.map((rule) => rule.external_system_id) ?? [];
  // get array of ext systems that we can create rules for
  const downstreamExternalSystems = allExternalSystems.filter((extSys) =>
    downStreamExternalSystemIntegrationTypes.includes(extSys.integrationType),
  );
  // get array of external systems that can have rules set up against them
  const externalSystemsWithNoRules = downstreamExternalSystems.filter(
    (externalSystem) => !externalSystemsInRules.includes(externalSystem.aggregatorId),
  );

  return (
    <div className={styles['container']}>
      <Typography size="xlarge" weight="bold">
        {cardTitle}
      </Typography>
      <Typography className={styles['subtitle']} size="medium">
        {cardSubtitle}
      </Typography>
      {menuItem.rules &&
        menuItem.rules.map((rule, index) => (
          <RulesCard
            externalSystemName={getExternalSystemName(rule.external_system_id, allExternalSystems)}
            key={index}
            rule={rule}
            defaultAttributes={itemDefaultAttributes}
            menuItemId={menuItem.itemId}
            setIsChannelAttributeGroupOpen={setIsChannelAttributeGroupOpen}
            setRuleToEdit={() => setRuleToEdit(rule)}
          />
        ))}
      {downstreamExternalSystems.length === 0 && (
        <EmptyState
          emptySectionText={'To use Rules, please add publishing channels to your brand within Admin'}
          Icon={ChannelIcon}
          buttonText="Go to Admin"
          onClick={() => {
            const adminUrl = window.location.origin.replace('hub', 'admin');
            window.location.assign(adminUrl);
          }}
        />
      )}
      {externalSystemsWithNoRules.length > 0 && (
        <EmptyStateButton label={addRuleLabel} onClick={() => setIsChannelAttributeGroupOpen(true)} />
      )}
      <CreateChannelAttributeGroupFlow
        open={isChannelAttributeGroupFlowOpen}
        onClose={onClose}
        externalSystems={externalSystemsWithNoRules}
        itemId={menuItem.itemId}
        itemDefaultAttributes={itemDefaultAttributes}
        ruleToEdit={ruleToEdit}
      />
    </div>
  );
};

export default RulesTab;
